// Purpose: /dropboxes/:id — dropbox detail with link, file list, rotate +
// revoke. Sender-side warning banner if any uploaded file has a dangerous
// extension (per Q2 decision: external uploaders DO NOT see this; only the
// receiver does).
// Phase 7.5 port: PageHeader + Card sections + Badge + Modal confirms.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getDropbox,
  getRoutingStatus,
  recoverDropboxLink,
  rotateDropbox,
  revokeDropbox,
  type DropboxDetail,
  type RoutingStatusSummary,
} from '../api/dropbox.ts';
import { AlertTriangleIcon } from '../components/icons/index.tsx';
import { Breadcrumb, PageHeader } from '../components/nav/PageHeader.tsx';
import { Badge, Button, Card, Modal } from '../components/ui/index.ts';
import {
  clearPendingRecoverLink,
  consumePendingRecoverLink,
  stashPendingRecoverLink,
} from '../lib/pendingActions.ts';

function fmt(bytes: string): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function dropboxBadge(status: string) {
  switch (status) {
    case 'ACTIVE':
      return <Badge tone="info">Active</Badge>;
    case 'PENDING_APPROVAL':
      return <Badge tone="warning">Pending approval</Badge>;
    case 'EXPIRED':
      return <Badge tone="neutral">Expired</Badge>;
    case 'REVOKED':
      return <Badge tone="danger">Revoked</Badge>;
    default:
      return <Badge tone="neutral">{status}</Badge>;
  }
}

export function DropboxDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DropboxDetail | null>(null);
  const [routing, setRouting] = useState<RoutingStatusSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // Commit 8b.1 link recovery.
  const [recoveringLink, setRecoveringLink] = useState(false);
  const [recoveredUrl, setRecoveredUrl] = useState<string | null>(null);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Commit 8b.1 rotate-success modal — explicit acknowledge. Carries
  // the new dropbox id so "Close" navigates to it instead of stranding
  // the operator on a REVOKED row.
  const [rotatedResult, setRotatedResult] = useState<{
    dropboxId: string;
    linkUrl: string;
  } | null>(null);

  async function reload() {
    if (!id) return;
    try {
      const fresh = await getDropbox(id);
      setData(fresh);
      // Routing status is best-effort — if the endpoint is unavailable (dev
      // with no worker running, or an admin-scope glitch) we still render the
      // rest of the page.
      try {
        const r = await getRoutingStatus(id);
        setRouting(r);
      } catch {
        setRouting(null);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    void reload();
  }, [id]);

  // Commit 8b.6: auto-continue Recover Link after the MFA round-trip.
  // See ShareDetail.tsx for the identical pattern on the share side.
  useEffect(() => {
    if (!id) return;
    const pending = consumePendingRecoverLink('dropbox', id);
    if (!pending) return;
    void handleRecover({ autoContinue: true });
  }, [id]);

  async function handleRotate() {
    if (!id) return;
    setWorking(true);
    setError(null);
    try {
      const res = await rotateDropbox(id);
      setConfirmRotate(false);
      // Commit 8b.1: do NOT navigate away automatically — the new URL
      // must stay on-screen until the operator explicitly acknowledges.
      // The Close handler on the rotate modal navigates.
      setRotatedResult({ dropboxId: res.dropboxId, linkUrl: res.linkUrl });
      setCopied(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  }

  async function handleRecover(opts: { autoContinue?: boolean } = {}): Promise<void> {
    if (!id) return;
    setRecoverError(null);
    setRecoveringLink(true);
    // Commit 8b.6: stash intent before POST so the step-up bounce
    // can auto-continue on return. See ShareDetail.tsx for the same
    // pattern + rationale.
    stashPendingRecoverLink('dropbox', id);
    try {
      const { publicUrl } = await recoverDropboxLink(id);
      setRecoveredUrl(publicUrl);
      setCopied(false);
      clearPendingRecoverLink();
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      // 428 → axios interceptor navigates to step-up; stash survives
      // for the post-mount useEffect to consume.
      if (ax.response?.status === 428) return;
      clearPendingRecoverLink();
      if (ax.response?.status === 409) {
        setRecoverError(
          'This dropbox was created before link recovery was enabled. Rotate the link to mint a recoverable one (the old link will stop working).'
        );
      } else if (ax.response?.status === 429) {
        setRecoverError(
          'Too many recovery attempts for this dropbox (3/hour limit). Wait an hour or ask an admin.'
        );
      } else {
        setRecoverError(
          opts.autoContinue
            ? `Auto-continue after MFA failed: ${(err as Error).message}`
            : (err as Error).message
        );
      }
    } finally {
      setRecoveringLink(false);
    }
  }

  async function handleRevoke() {
    if (!id) return;
    setWorking(true);
    setError(null);
    try {
      await revokeDropbox(id);
      await reload();
      setConfirmRevoke(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  }

  if (error && !data) {
    return (
      <div
        role="alert"
        className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
      >
        {error}
      </div>
    );
  }
  if (!data) return <p className="text-body-m text-fg-muted">Loading…</p>;

  const isActive = data.status === 'ACTIVE';
  const dangerous = data.files.filter((f) => f.dangerous);
  const showRoutingPanel = routing !== null && routing.routingTarget !== 'EPHEMERAL';

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <PageHeader
        breadcrumb={
          <Breadcrumb items={[{ label: 'Dropboxes', to: '/dropboxes' }, { label: data.label }]} />
        }
        title={data.label}
        subtitle={
          <>
            {data.type === 'EPHEMERAL' ? 'Ephemeral' : 'Persistent'}
            {' · '}
            {data.expiresAt ? `expires ${new Date(data.expiresAt).toLocaleString()}` : 'no expiry'}
          </>
        }
        actions={
          <>
            {dropboxBadge(data.status)}
            {isActive ? (
              <>
                <Button
                  variant="secondary"
                  size="md"
                  loading={recoveringLink}
                  disabled={working}
                  onClick={() => void handleRecover({})}
                >
                  Recover link
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  disabled={working}
                  onClick={() => setConfirmRotate(true)}
                >
                  Rotate link
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  disabled={working}
                  onClick={() => setConfirmRevoke(true)}
                >
                  Revoke
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {recoverError ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-warning/30 bg-status-warning-muted text-status-warning"
        >
          {recoverError}
        </div>
      ) : null}

      {dangerous.length > 0 ? (
        <div
          role="alert"
          className="flex items-start gap-3 px-4 py-3 rounded-md text-body-s border border-status-warning/30 bg-status-warning-muted text-status-warning"
        >
          <AlertTriangleIcon size={16} aria-hidden />
          <span>
            This dropbox contains {dangerous.length} file
            {dangerous.length === 1 ? '' : 's'} with an executable extension. Treat as untrusted
            before opening.
          </span>
        </div>
      ) : null}

      {showRoutingPanel && routing ? (
        <Card padding="comfortable">
          <div className="text-label uppercase text-fg-secondary mb-3">
            Routing to <span className="text-fg-primary">{routing.routingTarget}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <RoutingStat label="Routed" value={routing.routed} tone="ok" />
            <RoutingStat label="Pending" value={routing.pending} tone="info" />
            <RoutingStat
              label="Failed"
              value={routing.failed}
              tone={routing.failed > 0 ? 'warn' : 'mute'}
            />
            <RoutingStat
              label="Dead-lettered"
              value={routing.deadLettered}
              tone={routing.deadLettered > 0 ? 'warn' : 'mute'}
            />
          </div>
          <div className="mt-3 text-body-s text-fg-muted">
            Notifications: {routing.notifyOnSuccess ? 'success + ' : ''}
            {routing.notifyOnFailure ? 'failure' : routing.notifyOnSuccess ? '' : 'off'}
          </div>
        </Card>
      ) : null}

      {isActive ? (
        <Card padding="comfortable">
          <div className="text-label uppercase text-fg-secondary mb-2">Upload link</div>
          <div className="flex items-center gap-2">
            <code className="text-body-s break-all text-fg-muted">
              {`${window.location.origin}/d/<token>`}
            </code>
          </div>
          <div className="text-body-s text-fg-muted mt-2">
            The full link is shown at create time. To see it again, click{' '}
            <strong className="text-fg-primary">Recover link</strong> above (MFA-gated, 3/hour). To
            rotate to a fresh link, click <strong className="text-fg-primary">Rotate link</strong> —
            the old link stops working.
          </div>
        </Card>
      ) : null}

      <Card padding="none">
        <div className="p-5 pb-3">
          <h2 className="text-heading-m text-fg-primary">Files ({data.files.length})</h2>
        </div>
        {data.files.length === 0 ? (
          <div className="px-5 pb-5">
            <p className="text-body-m text-fg-secondary">No files uploaded yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {data.files.map((f) => (
              <li key={f.id} className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-body-m text-fg-primary truncate flex items-center gap-2">
                    {f.dangerous ? (
                      <AlertTriangleIcon
                        size={14}
                        className="text-status-warning flex-shrink-0"
                        aria-hidden
                      />
                    ) : null}
                    <span className="truncate">{f.originalFilename}</span>
                  </div>
                  <div className="text-body-s text-fg-muted">
                    {fmt(f.sizeBytes)} · {f.status} · uploaded{' '}
                    {new Date(f.uploadedAt).toLocaleString()}
                  </div>
                </div>
                {f.status === 'AVAILABLE' ? (
                  <a
                    href={`/api/dropboxes/${data.id}/uploads/${f.id}/download`}
                    className="inline-flex items-center h-8 px-3 rounded-md text-body-s font-semibold bg-bg-tertiary text-fg-primary hover:bg-bg-secondary transition-colors duration-fast"
                  >
                    Download
                  </a>
                ) : (
                  <span className="text-body-s text-fg-muted flex-shrink-0">{f.status}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {data.rotationChain.length > 1 ? (
        <Card padding="comfortable">
          <h2 className="text-heading-m text-fg-primary mb-3">Rotation history</h2>
          <ul className="text-body-s text-fg-secondary space-y-1">
            {data.rotationChain.map((c, i) => (
              <li key={c.id}>
                {i + 1}.{' '}
                {c.id === data.id ? (
                  <strong className="text-fg-primary">this dropbox</strong>
                ) : (
                  c.id
                )}
                {' · '}
                {c.status} · created {new Date(c.createdAt).toLocaleString()}
                {c.revokedAt ? ` · revoked ${new Date(c.revokedAt).toLocaleString()}` : ''}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* Commit 8b.1: recovered-link modal — same shape as ShareDetail.
          No auto-close. Link leaves DOM only on explicit Close. */}
      <Modal
        open={recoveredUrl !== null}
        onClose={() => {
          setRecoveredUrl(null);
          setCopied(false);
        }}
        title="Recovered dropbox link"
        size="md"
        footer={
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setRecoveredUrl(null);
              setCopied(false);
            }}
          >
            Close
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-body-m text-fg-secondary">
            Copy the URL now — closing this dialog clears it from the page. Recovering again
            requires another MFA step-up.
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 rounded-md bg-bg-tertiary text-body-s font-mono text-fg-primary break-all">
              {recoveredUrl}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                if (!recoveredUrl) return;
                try {
                  await navigator.clipboard.writeText(recoveredUrl);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Commit 8b.1: rotate-success modal. Explicit acknowledge so
          the new link isn't lost in the navigation. Close handler
          navigates to the new dropbox id. */}
      <Modal
        open={rotatedResult !== null}
        onClose={() => {
          if (rotatedResult) {
            window.location.href = `/dropboxes/${rotatedResult.dropboxId}`;
          }
        }}
        title="Your new dropbox link"
        size="md"
        footer={
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (rotatedResult) {
                window.location.href = `/dropboxes/${rotatedResult.dropboxId}`;
              }
            }}
          >
            I saved it — close
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-body-m text-fg-secondary">
            The old link stops working immediately. Save this link somewhere — you can use{' '}
            <strong className="text-fg-primary">Recover link</strong> on the new dropbox detail page
            to see it again later, but recovery requires MFA step-up.
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 rounded-md bg-bg-tertiary text-body-s font-mono text-fg-primary break-all">
              {rotatedResult?.linkUrl ?? ''}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                if (!rotatedResult) return;
                try {
                  await navigator.clipboard.writeText(rotatedResult.linkUrl);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        title="Rotate the link?"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmRotate(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={working}
              onClick={() => void handleRotate()}
            >
              Yes, rotate
            </Button>
          </>
        }
      >
        <p className="text-body-m text-fg-secondary">
          The current link stops working immediately. You will need to send the new link to anyone
          who still needs to upload.
        </p>
      </Modal>

      <Modal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        title="Revoke this dropbox?"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmRevoke(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={working}
              onClick={() => void handleRevoke()}
            >
              Yes, revoke
            </Button>
          </>
        }
      >
        <p className="text-body-m text-fg-secondary">
          The link stops working immediately. Already-uploaded files remain available to you.
        </p>
      </Modal>
    </div>
  );
}

function RoutingStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'info' | 'warn' | 'mute';
}) {
  const color =
    tone === 'ok'
      ? 'text-status-success'
      : tone === 'warn'
        ? 'text-status-warning'
        : tone === 'info'
          ? 'text-fg-secondary'
          : 'text-fg-muted';
  return (
    <div>
      <div className={`text-heading-l font-semibold ${color}`}>{value}</div>
      <div className="text-body-s text-fg-muted">{label}</div>
    </div>
  );
}
